import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ChevronDown, Search, Check, X } from 'lucide-react';
import styles from './MultiSelect.module.scss';

const MAX_VISIBLE_CHIPS = 3;

const MultiSelect = ({
  label,
  placeholder,
  value,
  onChange,
  options,
  searchValue,
  onSearchChange,
  loading,
  emptyMessage,
  loadingMessage,
  disabled,
  error,
  helperText,
  id,
  className,
  onSelectedClick
}) => {
  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchInputRef = useRef(null);

  // The trigger is a <div role="combobox">, which a <label for> cannot
  // name: tie the label to it with aria-labelledby, the popup with
  // aria-controls, and the error / helper text with aria-describedby
  // (QA CM-19: the chat "Assign to" picker had no accessible name).
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const messageId = error || helperText ? `${baseId}-msg` : undefined;

  // Popup is rendered into a portal at <body> so it escapes any ancestor
  // `overflow: hidden` (e.g. PagePanel's clip rule) and stacking contexts.
  // Its position is computed from the trigger's bounding rect and kept in
  // sync on scroll/resize while open.
  const [popupRect, setPopupRect] = useState(null);

  // Walk up the DOM looking for a Bootstrap modal container. When the
  // trigger lives inside a Modal, we constrain the popup to the dialog's
  // bounds so it stays visually associated with the dialog instead of
  // spilling out below it (the trigger commonly sits near the modal
  // footer, leaving little room beneath).
  const findModalRect = (node) => {
    let n = node?.parentElement;
    while (n && n !== document.body) {
      if (
        n.classList &&
        (n.classList.contains('modal-content') ||
          n.classList.contains('modal-dialog'))
      ) {
        return n.getBoundingClientRect();
      }
      n = n.parentElement;
    }
    return null;
  };

  const POPUP_MAX_HEIGHT = 320;
  const POPUP_MIN_HEIGHT = 160;
  const GUTTER = 6;

  const updatePopupRect = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const modalRect = findModalRect(trigger);
    const lower = modalRect
      ? Math.min(modalRect.bottom, window.innerHeight)
      : window.innerHeight;
    const upper = modalRect ? Math.max(modalRect.top, 0) : 0;
    const spaceBelow = lower - r.bottom - GUTTER;
    const spaceAbove = r.top - upper - GUTTER;

    let placeAbove;
    let maxHeight;
    if (spaceBelow >= POPUP_MAX_HEIGHT) {
      placeAbove = false;
      maxHeight = POPUP_MAX_HEIGHT;
    } else if (spaceAbove >= POPUP_MAX_HEIGHT) {
      placeAbove = true;
      maxHeight = POPUP_MAX_HEIGHT;
    } else if (spaceAbove > spaceBelow) {
      placeAbove = true;
      maxHeight = Math.max(POPUP_MIN_HEIGHT, spaceAbove);
    } else {
      placeAbove = false;
      maxHeight = Math.max(POPUP_MIN_HEIGHT, spaceBelow);
    }

    setPopupRect({
      // Anchor the bottom of the popup just above the trigger when flipped
      // so the popup grows upward and stays flush with the trigger
      // regardless of its rendered height.
      top: placeAbove ? null : r.bottom + GUTTER,
      bottom: placeAbove ? window.innerHeight - r.top + GUTTER : null,
      left: r.left,
      width: r.width,
      maxHeight,
    });
  };

  const isControlledSearch = typeof searchValue === 'string';
  const search = isControlledSearch ? searchValue : internalSearch;

  const setSearch = (q) => {
    if (!isControlledSearch) setInternalSearch(q);
    onSearchChange?.(q);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Trigger lives in the wrapper; the popup is portaled to <body>.
      // A click inside either is "inside" — anything else closes the menu.
      const inTrigger =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Lock the popup to the trigger's current rect while open. Runs after
  // layout so the first paint is placed correctly, then stays synced with
  // window + scrollable-ancestor scroll/resize.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopupRect();
    const scrollParents = [];
    let node = triggerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }
    const handler = () => updatePopupRect();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    scrollParents.forEach((n) => n.addEventListener('scroll', handler));
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      scrollParents.forEach((n) => n.removeEventListener('scroll', handler));
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const optionMap = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(String(o.value), o));
    return map;
  }, [options]);

  const selectedOptions = useMemo(() => {
    return value.map((v) => {
      const o = optionMap.get(String(v));
      return o || { value: v, label: String(v) };
    });
  }, [value, optionMap]);

  const filteredOptions = useMemo(() => {
    if (isControlledSearch) return options;
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label || ''} ${o.sublabel || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search, isControlledSearch]);

  const toggle = (val) => {
    const exists = value.some((v) => String(v) === String(val));
    const next = exists
      ? value.filter((v) => String(v) !== String(val))
      : [...value, val];
    onChange?.(next);
  };

  const removeChip = (e, val) => {
    e.stopPropagation();
    onChange?.(value.filter((v) => String(v) !== String(val)));
  };

  const clearAll = () => onChange?.([]);

  const triggerClasses = [
    styles.trigger,
    open ? styles.triggerOpen : '',
    error ? styles.triggerError : '',
    disabled ? styles.triggerDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [styles.wrapper, className || ''].filter(Boolean).join(' ');

  const visibleChips = selectedOptions.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = selectedOptions.length - visibleChips.length;

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      {label && (
        <label id={labelId} htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={label ? labelId : undefined}
        aria-controls={listboxId}
        aria-describedby={messageId}
        aria-invalid={error ? true : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className={triggerClasses}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        {selectedOptions.length === 0 ? (
          <span className={styles.placeholder}>{placeholder}</span>
        ) : (
          <>
            {visibleChips.map((o) => (
              <span key={o.value} className={styles.chip}>
                <span className={styles.chipLabel}
                onClick={(e)=>{
                  if(onSelectedClick){
                    e.stopPropagation();
                  onSelectedClick(o)
                  }
                }}
                >{o.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${o.label}`}
                    className={styles.chipRemove}
                    onClick={(e) => removeChip(e, o.value)}
                  >
                    <X size={11} />
                  </button>
                )}
              </span>
            ))}
            {overflowCount > 0 && <span className={styles.chipMore}>+{overflowCount}</span>}
          </>
        )}
        <span
          className={[styles.caret, open ? styles.caretOpen : ''].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {open && popupRect &&
        createPortal(
          <div
            ref={popupRef}
            id={listboxId}
            className={styles.popup}
            role="listbox"
            aria-label={label || undefined}
            aria-multiselectable="true"
            style={{
              position: 'fixed',
              ...(popupRect.top != null ? { top: popupRect.top } : {}),
              ...(popupRect.bottom != null ? { bottom: popupRect.bottom } : {}),
              left: popupRect.left,
              width: popupRect.width,
              maxHeight: popupRect.maxHeight,
            }}
          >
          <div className={styles.searchBar}>
            <span className={styles.searchIcon}>
              <Search size={14} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search…"
              aria-label={label ? `Search ${label}` : 'Search options'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className={styles.loadingRow}>{loadingMessage}</div>
          ) : filteredOptions.length === 0 ? (
            <div className={styles.empty}>{emptyMessage}</div>
          ) : (
            <ul className={styles.list}>
              {filteredOptions.map((o) => {
                const checked = value.some((v) => String(v) === String(o.value));
                const cls = [styles.option, checked ? styles.optionSelected : '']
                  .filter(Boolean)
                  .join(' ');
                return (
                  <li
                    key={o.value}
                    role="option"
                    aria-selected={checked}
                    className={cls}
                    onClick={() => toggle(o.value)}
                  >
                    <span
                      className={[
                        styles.optionCheckbox,
                        checked ? styles.optionCheckboxChecked : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className={styles.optionBody}>
                      <div className={styles.optionLabel}>{o.label}</div>
                      {o.sublabel && <div className={styles.optionSub}>{o.sublabel}</div>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className={styles.footer}>
            <span>
              {selectedOptions.length} selected
              {filteredOptions.length > 0 && !loading
                ? ` · ${filteredOptions.length} option${filteredOptions.length === 1 ? '' : 's'}`
                : ''}
            </span>
            <button
              type="button"
              className={styles.footerAction}
              onClick={clearAll}
              disabled={selectedOptions.length === 0}
            >
              Clear all
            </button>
          </div>
          </div>,
          document.body
        )}

      {error && <span id={messageId} className={styles.error}>{error}</span>}
      {!error && helperText && (
        <span id={messageId} className={styles.helperText}>{helperText}</span>
      )}
    </div>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      sublabel: PropTypes.string,
    })
  ),
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  onSelectedClick: PropTypes.func,
};

MultiSelect.defaultProps = {
  label: '',
  placeholder: 'Select…',
  value: [],
  options: [],
  searchValue: undefined,
  onSearchChange: undefined,
  loading: false,
  emptyMessage: 'No options found',
  loadingMessage: 'Loading…',
  disabled: false,
  error: '',
  helperText: '',
  id: undefined,
  className: '',
  onSelectedClick: undefined,
};

export default MultiSelect;
